import { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaWarehouse, FaMapMarkerAlt, FaUserTie, FaCalendarAlt } from "react-icons/fa";
import {
  getAllEntrepots,
  createEntrepot,
  updateEntrepot,
  deleteEntrepot
} from "../../services/entrepots";
import { getAllLocalisations } from "../../services/localisations";
import { getAllUsers } from "../../services/utilisateurs";
import type { Entrepot, EntrepotPayload } from "../../types/entrepot";
import type { Localisation } from "../../types/localisation";
import type { Utilisateur } from "../../types/utilisateur";
import "./Entrepots.css";

export default function Entrepots() {
  const [entrepots, setEntrepots] = useState<Entrepot[]>([]);
  const [localisations, setLocalisations] = useState<Localisation[]>([]);
  const [utilisateurs, setUtilisateurs] = useState<Utilisateur[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEntrepot, setSelectedEntrepot] = useState<Entrepot | null>(null);

  useEffect(() => {
    const safeSetArray = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (data: unknown) => {
      if (Array.isArray(data)) setter(data as T[]);
      else setter([]);
    };

    const fetchData = async () => {
      const dataEntrepots = await getAllEntrepots();
      const dataLoc = await getAllLocalisations();
      const dataUsers = await getAllUsers();
      safeSetArray<Entrepot>(setEntrepots)(dataEntrepots);
      safeSetArray<Localisation>(setLocalisations)(dataLoc);
      safeSetArray<Utilisateur>(setUtilisateurs)(dataUsers);
    };
    fetchData();
  }, []);


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const entrepotPayload: EntrepotPayload = {
      nom: formData.get("nom") as string,
      localisation_id: formData.get("localisation") as string,
      responsable_id: formData.get("responsable") as string,
      utilisateurs_affectes_ids: formData.getAll("utilisateurs_affectes") as string[],
    };

    if (selectedEntrepot) {
      await updateEntrepot(selectedEntrepot.id, entrepotPayload);
    } else {
      await createEntrepot(entrepotPayload);
    }

    const data = await getAllEntrepots();
    setEntrepots(data);
    setShowModal(false);
    setSelectedEntrepot(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Supprimer cet entrepôt ?")) {
      await deleteEntrepot(id);
      setEntrepots(entrepots.filter((e) => e.id !== id));
    }
  };

  return (
    <div className="entrepots">
      <div className="entrepots-header">
        <button className="btn-ajout" onClick={() => setShowModal(true)}>
          ➕ Ajouter entrepôt
        </button>
      </div>

      <div className="entrepots-grid">
        {entrepots.map((e) => (
          <div key={e.id} className="entrepot-card">
            <h3 className="badge-entrepot">
              <FaWarehouse className="icon-badge" /> {e.nom}
            </h3>
            <p className="card-line">
              <FaMapMarkerAlt className="icon-line" /> {e.localisation?.nom}
            </p>
            <p className="card-line">
              <FaUserTie className="icon-line" /> {e.responsable ? `${e.responsable.first_name} ${e.responsable.last_name}` : "-"}
            </p>
            <p className="card-line">
              👥 {e.utilisateurs_affectes.length > 0 
                ? e.utilisateurs_affectes.map(u => `${u.first_name} ${u.last_name}`).join(", ") 
                : "-"}
            </p>
            <p className="card-line">
              <FaCalendarAlt className="icon-line" /> {new Date(e.date_creation).toLocaleDateString()}
            </p>
            <div className="card-actions">
              <FaEdit
                className="icon-edit"
                onClick={() => {
                  setSelectedEntrepot(e);
                  setShowModal(true);
                }}
              />
              <FaTrash
                className="icon-trash"
                onClick={() => handleDelete(e.id)}
              />
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{selectedEntrepot ? "Modifier entrepôt" : "Ajouter entrepôt"}</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="nom"
                placeholder="Nom de l'entrepôt"
                defaultValue={selectedEntrepot?.nom}
                required
              />

              <select name="localisation" defaultValue={selectedEntrepot?.localisation?.id || ""} required>
                <option value="">-- Choisir localisation --</option>
                {localisations.length > 0 ? (
                  localisations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.nom}</option>
                  ))
                ) : (
                  <option disabled>Aucune localisation disponible</option>
                )}
              </select>

              <select name="responsable" defaultValue={selectedEntrepot?.responsable?.id || ""}>
                <option value="">-- Choisir responsable --</option>
                {utilisateurs.length > 0 ? (
                  utilisateurs.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </option>
                  ))
                ) : (
                  <option disabled>Aucun utilisateur disponible</option>
                )}
              </select>

              <label className="modal-label">Utilisateurs concernés :</label>
              <div className="checkbox-group">
                {utilisateurs.length > 0 ? (
                  utilisateurs.map((u) => (
                    <label key={u.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        name="utilisateurs_affectes"
                        value={u.id}
                        defaultChecked={selectedEntrepot?.utilisateurs_affectes.some(
                          (aff) => aff.id === u.id
                        )}
                      />
                      {u.first_name} {u.last_name}
                    </label>
                  ))
                ) : (
                  <p className="empty-list">Aucun utilisateur disponible</p>
                )}
              </div>

              <div className="modal-actions">
                <button type="submit">Enregistrer</button>
                <button type="button" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
